/**
 * Service for polling audit results
 * Simple polling approach that's reliable and debuggable
 */
import { AuditState } from "../types/epc-interfaces";
import { ensureHttpsProtocol } from "../utils/ensureHttpsProtocol";

const API_HOST = process.env.NEXT_PUBLIC_LL_WEB_ENCOMPASS_API_HOST;

interface AuditUpdateEvent {
  auditState: AuditState;
  transactionStateId: string;
  updated: string;
  errorMessage?: string | null;
  errorCode?: string | null;
  errorStep?: string | null;
  errorTimestamp?: string | null;
  progressStep?: string | null;
  progressMessage?: string | null;
  progressPercent?: number | null;
  progressUpdatedAt?: string | null;
}

export class AuditProgressService {
  private auditStateHandler?: (
    state: AuditState,
    transactionStateId: string,
    progressData?: {
      progressStep?: string | null;
      progressMessage?: string | null;
      progressPercent?: number | null;
    },
  ) => void;
  private errorHandler?: (error: Error) => void;
  private transactionId?: string;
  private token?: string;
  private sessionId?: string | null;
  private isPolling = false;
  private startTime?: number;
  private timeoutMs?: number;
  private timeoutId?: number;
  private pollIntervalId?: number;
  private readonly pollIntervalMs = 3000; // Poll every 3 seconds

  /**
   * Stale Data Detection:
   * When triggering a rerun, the OLD transactionStateId is known.
   * If poll returns Complete/Failed with SAME ID, it's stale data.
   * Only trust results when we see a NEW transactionStateId.
   */
  private previousTransactionStateId?: string;
  private seenNewTransactionStateId = false;

  constructor() {}

  /**
   * Start polling for audit updates
   * @param previousTransactionStateId - If provided, poll results with this ID
   *   and a terminal state (Complete/Failed) will be treated as stale and ignored.
   *   This handles the rerun case where old audit data may be returned before
   *   the new audit record is created.
   */
  async startStreaming(
    transactionId: string,
    token: string,
    auditStateHandler: (
      state: AuditState,
      transactionStateId: string,
      progressData?: {
        progressStep?: string | null;
        progressMessage?: string | null;
        progressPercent?: number | null;
      },
    ) => void,
    initialDelayMs: number = 2000,
    timeoutMs: number = 1800000, // 30 minutes
    onTimeout?: (error: Error) => void,
    previousTransactionStateId?: string,
    sessionId?: string | null,
  ): Promise<void> {
    console.log("[AuditProgressService] Starting polling", {
      transactionId,
      initialDelayMs,
      timeoutMs,
      pollIntervalMs: this.pollIntervalMs,
      wasAlreadyPolling: this.isPolling,
      previousTransactionStateId:
        previousTransactionStateId || "(none - fresh audit)",
    });

    if (this.isPolling) {
      this.stopStreaming();
    }

    this.transactionId = transactionId;
    this.token = token;
    this.sessionId = sessionId;
    this.auditStateHandler = auditStateHandler;
    this.errorHandler = onTimeout;
    this.isPolling = true;
    this.startTime = Date.now();
    this.timeoutMs = timeoutMs;
    this.previousTransactionStateId = previousTransactionStateId;
    this.seenNewTransactionStateId = false;

    // Wait for initial delay to allow transaction record to be created
    await new Promise((resolve) => setTimeout(resolve, initialDelayMs));

    if (!this.isPolling) {
      console.log(
        "[AuditProgressService] Polling stopped during initial delay",
      );
      return;
    }

    // Start polling
    this.startPolling();
  }

  stopStreaming(): void {
    console.log("[AuditProgressService] Stopping polling");

    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId);
      this.pollIntervalId = undefined;
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }

    this.isPolling = false;
    this.auditStateHandler = undefined;
    this.transactionId = undefined;
    this.token = undefined;
    this.previousTransactionStateId = undefined;
    this.seenNewTransactionStateId = false;
  }

  private startPolling(): void {
    if (!this.transactionId || !this.token) {
      console.error("[AuditProgressService] Missing transaction ID or token");
      return;
    }

    // Setup timeout
    this.setupTimeout();

    // Poll immediately
    void this.poll();

    // Then poll every 3 seconds
    this.pollIntervalId = window.setInterval(() => {
      void this.poll();
    }, this.pollIntervalMs);
  }

  private async poll(): Promise<void> {
    if (!this.isPolling || !this.transactionId || !this.token) {
      return;
    }

    try {
      const url = ensureHttpsProtocol(
        `${API_HOST}/integrations/audit-details/${this.transactionId}`,
      );

      console.log(`[AuditProgressService] Polling: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json",
          ...(this.sessionId && { "x-session-id": this.sessionId }),
        },
      });

      console.log(`[AuditProgressService] Response status: ${response.status}`);

      if (!response.ok) {
        if (response.status === 404) {
          console.log(
            "[AuditProgressService] Audit not found yet (404), will retry...",
          );
          return;
        }
        console.error(
          `[AuditProgressService] Poll failed with status ${response.status}`,
        );
        return;
      }

      const data = (await response.json()) as AuditUpdateEvent | null;

      if (!data) {
        console.log("[AuditProgressService] No data returned, will retry...");
        return;
      }

      console.log("[AuditProgressService] Received poll response:", {
        auditState: data.auditState,
        transactionStateId: data.transactionStateId,
        progressPercent: data.progressPercent,
        progressMessage: data.progressMessage,
        previousTransactionStateId: this.previousTransactionStateId,
        seenNewTransactionStateId: this.seenNewTransactionStateId,
      });

      // STALE DATA DETECTION:
      // If we're doing a rerun (previousTransactionStateId is set),
      // and we get back the OLD transactionStateId with a terminal state,
      // this is stale data from the previous audit - ignore it.
      const isTerminalState =
        data.auditState === AuditState.Complete ||
        data.auditState === AuditState.Failed ||
        data.auditState === AuditState.Error;
      const isSameAsOldAudit =
        this.previousTransactionStateId &&
        data.transactionStateId === this.previousTransactionStateId;
      const isStaleData =
        isSameAsOldAudit && isTerminalState && !this.seenNewTransactionStateId;

      if (isStaleData) {
        console.log(
          "[AuditProgressService] STALE DATA DETECTED - ignoring old audit",
          {
            reason:
              "Terminal state with same transactionStateId as previous audit",
            previousTransactionStateId: this.previousTransactionStateId,
            returnedTransactionStateId: data.transactionStateId,
            returnedState: data.auditState,
          },
        );
        // Don't process this data, just wait for new audit to appear
        return;
      }

      // Track if we've seen a new transactionStateId (only log once)
      if (
        this.previousTransactionStateId &&
        data.transactionStateId !== this.previousTransactionStateId &&
        !this.seenNewTransactionStateId
      ) {
        console.log(
          "[AuditProgressService] New transactionStateId detected - trusting all future results",
          {
            previousTransactionStateId: this.previousTransactionStateId,
            newTransactionStateId: data.transactionStateId,
          },
        );
        this.seenNewTransactionStateId = true;
      }

      this.handleAuditUpdate(data);

      // Stop polling if audit is complete or failed
      if (isTerminalState) {
        console.log(
          `[AuditProgressService] Audit finished: ${data.auditState}`,
        );
        this.stopStreaming();
      }
    } catch (error) {
      console.error("[AuditProgressService] Poll error:", error);
      // Continue polling on error - might be a transient network issue
    }
  }

  private handleAuditUpdate(data: AuditUpdateEvent): void {
    if (!this.auditStateHandler) {
      console.warn(
        "[AuditProgressService] No audit state handler set, ignoring update",
      );
      return;
    }

    const progressData = {
      progressStep: data.progressStep,
      progressMessage: data.progressMessage,
      progressPercent: data.progressPercent,
    };

    this.auditStateHandler(
      data.auditState,
      data.transactionStateId,
      progressData,
    );
  }

  private setupTimeout(): void {
    if (!this.startTime || !this.timeoutMs) {
      return;
    }

    const elapsed = Date.now() - this.startTime;
    const remaining = this.timeoutMs - elapsed;

    if (remaining <= 0) {
      console.error("[AuditProgressService] Audit already timed out");
      this.handleTimeout();
      return;
    }

    console.log(
      `[AuditProgressService] Setting timeout for ${remaining}ms (${Math.round(remaining / 1000)}s)`,
    );

    this.timeoutId = window.setTimeout(() => {
      console.error(
        `[AuditProgressService] Audit timed out after ${this.timeoutMs}ms`,
      );
      this.handleTimeout();
    }, remaining);
  }

  private handleTimeout(): void {
    this.stopStreaming();
    if (this.errorHandler) {
      this.errorHandler(
        new Error(
          "Audit is taking longer than expected. This may indicate a processing issue. Please try starting a new audit or contact support if the problem persists.",
        ),
      );
    }
  }

  static create(): AuditProgressService {
    return new AuditProgressService();
  }
}
